import type { FC } from 'react';

import { useTranslation } from '~/hooks';
import { sanitize } from '~/utils/sanitize';

import { FlexIconTextSub } from './FlexIconTextSub';
import { DescriptionHeading, DescriptionItem, DescriptionText, DescriptionWrapper } from './styled';

interface MissionDescriptionProps {
  missionIntro: string;
  missionDesc: string;
  requiredCertificates?: string;
  requiredExperience?: string;
  missionBonuses?: string;
  missionPrerequisites?: string;
  missionLegalInfos?: string;
  workRates?: string;
}

export const MissionDescription: FC<MissionDescriptionProps> = ({
  missionIntro,
  missionDesc,
  requiredCertificates,
  requiredExperience,
  missionBonuses,
  missionPrerequisites,
  missionLegalInfos,
  workRates,
}) => {
  const t = useTranslation();

  return (
    <DescriptionWrapper>
      {missionIntro ? (
        <DescriptionItem>
          <DescriptionText
            dangerouslySetInnerHTML={{
              __html: sanitize(missionIntro),
            }}
          />
        </DescriptionItem>
      ) : null}
      <DescriptionItem>
        <DescriptionHeading className="desc-heading" variant="heading-4" forwardedAs="h2">
          {t('missions.about')}
        </DescriptionHeading>
        <DescriptionText
          dangerouslySetInnerHTML={{
            __html: sanitize(missionDesc),
          }}
        />
      </DescriptionItem>
      {workRates ? (
        <DescriptionItem>
          <DescriptionText
            dangerouslySetInnerHTML={{
              __html: sanitize(workRates),
            }}
          />
        </DescriptionItem>
      ) : null}
      {missionBonuses ? (
        <DescriptionItem>
          <DescriptionHeading className="desc-heading" variant="heading-4" forwardedAs="h2">
            {t('missions.bonuses')}
          </DescriptionHeading>
          <DescriptionText
            dangerouslySetInnerHTML={{
              __html: sanitize(missionBonuses),
            }}
          />
        </DescriptionItem>
      ) : null}
      {(missionPrerequisites || requiredCertificates || requiredExperience || missionLegalInfos) && (
        <DescriptionItem>
          <DescriptionHeading className="desc-heading" variant="heading-4" forwardedAs="h2">
            {t('missions.profile')}
          </DescriptionHeading>
          {missionPrerequisites && (
            <DescriptionText
              dangerouslySetInnerHTML={{
                __html: sanitize(missionPrerequisites),
              }}
            />
          )}
          {requiredCertificates && (
            <FlexIconTextSub
              title={t('missions.certificates')}
              subtitle={requiredCertificates || t('missions.no_certificates_required')}
              iconName="award-filled"
              iconColor="purple"
            />
          )}
          {requiredExperience && (
            <FlexIconTextSub
              title={t('missions.experience')}
              subtitle={requiredExperience || t('missions.no_experience_required')}
              iconName="work-filled"
              iconColor="blue"
            />
          )}
          {missionLegalInfos && (
            <DescriptionText
              dangerouslySetInnerHTML={{
                __html: sanitize(missionLegalInfos),
              }}
            />
          )}
        </DescriptionItem>
      )}
    </DescriptionWrapper>
  );
};
